import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  VStack,
  HStack,
  Text,
  Button,
  Radio,
  RadioGroup,
  Box,
  useToast,
} from '@chakra-ui/react';
import {
  GoogleCircleIcons,
  ICloudIcon,
  OutLookCircle,
  SimpleCloseIcon,
} from '@mybridge/icons'; // Replace with actual path
import { ExchangeLogo } from '@mybridge/icons/ExchangeLogo';
import { useSelector, useDispatch } from 'react-redux';
import { updateUserInfo } from 'v4/store/actions/user-profile.actions'; // Assuming you have this action
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

const AddCalendarSelectionModal = ({ isOpen, onClose, onUpdate,handleGetCalendarList,fromNoCalendar,handleUpdateCalendar }) => {
  const { calendarList } = useSelector((state) => state.settings);
  const dispatch = useDispatch(); // Initialize dispatch for calling the API
  const toast = useToast();
  const { t } = useTranslator();

  // Find the initially selected calendar from calendarList
  const getInitialSelectedCalendar = () => {
    return (
      calendarList.find((calendar) => calendar.selected)?.provider || 'none'
    );
  };

  // Use the initial selected calendar for the state
  const [selectedCalendar, setSelectedCalendar] = useState(
    getInitialSelectedCalendar()
  );

  // Update selectedCalendar when calendarList or modal visibility changes
  useEffect(() => {
    if (isOpen) {
      const initialSelected = getInitialSelectedCalendar();
      setSelectedCalendar(initialSelected);
    }
  }, [isOpen, calendarList]);

  // Icons for each calendar type
  const calendarOptions = [
    {
      id: 'Google',
      label: 'johndor123@gmail.com',
      icon: <GoogleCircleIcons />,
    },
    {
      id: 'Microsoft',
      label: t('otherModules.outlookPlugin') || 'Outlook plug-in (Windows)',
      icon: <OutLookCircle width="28" height="28" />,
    },
    {
      id: 'Exchange',
      label: t('otherModules.exchangePlugin') || 'Exchange Calendar plug-in (Windows)',
      icon: <ExchangeLogo />,
    },
    {
      id: 'iCloud',
      label: t('otherModules.iCloudPlugin') || 'iCloud Calendar plug-in (Windows)',
      icon: <ICloudIcon />,
    },
    { id: 'none', label: t('otherModules.doNotAddEvents') || 'Do not add new events to a calendar' },
  ];

  // Dynamically set the label from calendarList
  const calendarOptionsWithLabel = calendarOptions.map((option) => {
    const matchedCalendar = calendarList.find(
      (calendar) => calendar.provider.toLowerCase() === option.id.toLowerCase()
    );
    return matchedCalendar
      ? {
          ...option,
          label: matchedCalendar.calendar_id || matchedCalendar.email,
        }
      : option;
  });

  // Filter only connected calendars based on calendarList, always include 'none'
  const filteredCalendarOptions = [
    ...calendarOptionsWithLabel.filter((option) =>
      calendarList.some(
        (calendar) =>
          calendar.provider.toLowerCase() === option.id.toLowerCase()
      )
    ),
    { id: 'none', label: t('otherModules.doNotAddEvents') || 'Do not add new events to a calendar' },
  ];

  const handleUpdateClick = () => {
    // Prepare the payload: set `id` to `null` if "none" is selected
    const selectedId =
      selectedCalendar === 'none'
        ? null
        : calendarList.find(
            (calendar) => calendar.provider.toLowerCase() === selectedCalendar
          )?.id;

    // Dispatch the API call to update the appointment or user info
    dispatch(
      updateUserInfo({
        calendar: selectedId,
        default_calendar: true,
      })
    )
      .then((res) => {
        if (!res.payload.error) {
          toast({
            title: t('otherModules.calendarUpdated') || 'Calendar updated successfully',
            description: "",
            status: 'success',
            duration: 9000,
            isClosable: true,
          });
         !fromNoCalendar&&onUpdate(selectedCalendar);
         !!fromNoCalendar&&handleUpdateCalendar(selectedCalendar)
          handleGetCalendarList()
        } else {
          // console.log('Error updating calendar:', res.payload.error);
        }
      })
      .catch((err) => {
        console.error('Error during updateAppointments API call:', err);
      });

    // Close the modal after the update
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxW="502px" px={'16px'} pt={'16px'} pb={'30px'}>
        <ModalHeader
          fontSize="30"
          fontWeight={400}
          color="#5B5B5B"
          pt="30px"
          lineHeight={'36px'}
        >
          {t('otherModules.whichCalendarAdd') || "Which calendar should we add new events to?"}
        </ModalHeader>
        <Box
          cursor={'pointer'}
          onClick={onClose}
          position="absolute"
          right="2%"
          top="2%"
        >
          <SimpleCloseIcon width="15" height="15" color="#747474" />
        </Box>

        <ModalBody >
          <RadioGroup onChange={setSelectedCalendar} value={selectedCalendar}>
            <VStack align="start" spacing={4}>
              {filteredCalendarOptions.map((option) => (
                <HStack key={option.id.toLowerCase()} alignItems="start">
                  <Radio value={option.id.toLowerCase()} />
                  {option.icon && <Box mt={-1}>{option.icon}</Box>}
                  <Text
                    // w={'380px'}
                    fontSize="18px"
                    color="#5B5B5B"
                    lineHeight={'21.6px'}
                  >
                    {option.label.length>35?option.label.slice(0,35)+"...":option.label}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </RadioGroup>

          <Text fontSize="16px" mt={4} color="#5B5B5B" lineHeight={'19.2px'}>
            {t('settingsIntegration.usePluginToSelectCalendars') || "Use plug-in to select specific calendars."}
          </Text>
        </ModalBody>

        <ModalFooter justifyContent="start">
          <Button
            fontSize={'18px'}
            color="#3D5A80"
            p={'9px 30px'}
            _hover={{ bgColor: '#E4E6EB' }}
            lineHeight={'20px'}
            variant="outline"
            onClick={onClose}
            bgColor="#E4E6EB"
          >
            {t('buttons.cancel') || "Cancel"}
          </Button>
          <Button
            color={'#FFFFFF'}
            fontSize={'18px'}
            ml={4}
            lineHeight={'20px'}
            p={'10px 30px'}
            _hover={{ bgColor: '#3D5A80' }}
            variant="outline"
            bgColor="#3D5A80"
            onClick={handleUpdateClick}
          >
            {t('createPost.update') || "Update"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
export default AddCalendarSelectionModal;
